if (!customElements.get('christmas-product-feature')) {
  customElements.define(
    'christmas-product-feature',
    class ChristmasProductFeature extends window.BAO.CustomElement {
      connectedCallback () {
        super.connectedCallback()

        this.isTicking = false
        this.lastKnownY = window.scrollY

        this.requestTick = this.requestTick.bind(this)
        this.updateEl = this.updateEl.bind(this)

        this.updateEl()
      }

      setupListeners () {
        super.setupListeners()

        this.onScroll = this.onScroll.bind(this)

        window.addEventListener('scroll', this.onScroll, { passive: true })

        this.els['product-trigger']?.elements.forEach(el => {
          this.listeners.add(el, 'click', this.openProductDrawer.bind(this))
        })

        this.initialiseCollectionCarousel()

        this.initialiseProductCarousels()
      }

      onScroll () {
        this.lastKnownY = window.scrollY

        this.requestTick()
      }

      requestTick () {
        if (!this.isTicking) requestAnimationFrame(this.updateEl)

        this.isTicking = true
      }

      updateEl () {
        this.isTicking = false

        this.animateHero()
        this.animateContent()
        this.animateElements()
      }

      animateHero () {
        const heroEl = this.els.hero?.element

        if (!heroEl) return

        const imageEl = this.els['hero-image']?.element
        const textEl = this.els['hero-text']?.element

        const containerOffsetTop = getOffsetTop(heroEl)
        const elementStartPoint = containerOffsetTop - window.innerHeight / 2
        const elementEndPoint =
          containerOffsetTop + heroEl.offsetHeight / 2 - window.innerHeight / 2

        const scaleToEnd = 1
        const amountToScale = 0.5

        if (this.lastKnownY <= elementStartPoint) {
          if (imageEl) {
            imageEl.style.transform = `scale(${scaleToEnd + amountToScale})`
          }
          if (textEl) {
            textEl.style.transform = `scale(${scaleToEnd - amountToScale})`
            textEl.style.opacity = 0
          }

          return
        }

        if (this.lastKnownY > elementEndPoint) {
          if (imageEl) {
            imageEl.style.transform = `scale(${scaleToEnd})`
          }
          if (textEl) {
            textEl.style.transform = `scale(${scaleToEnd})`
            textEl.style.opacity = 1
          }

          return
        }

        const totalScrollAmount = elementEndPoint - elementStartPoint

        const scrollYAsAPercentageOfJourney =
          (this.lastKnownY - elementStartPoint) / totalScrollAmount

        const scaleAmount = amountToScale * scrollYAsAPercentageOfJourney

        if (imageEl) {
          imageEl.style.transform = `scale(${scaleToEnd +
            amountToScale -
            scaleAmount})`
        }
        if (textEl) {
          textEl.style.transform = `scale(${scaleToEnd -
            amountToScale +
            scaleAmount})`
          textEl.style.opacity = scrollYAsAPercentageOfJourney
        }
      }

      animateContent () {
        const contentEl = this.els.content?.element

        if (!contentEl) return

        const containerOffsetTop = getOffsetTop(contentEl)
        const elementStartPoint = containerOffsetTop - window.innerHeight * 0.75
        const elementEndPoint = elementStartPoint + window.innerHeight / 2

        const scale = 0.15
        const shadow = 50

        if (this.lastKnownY <= elementStartPoint) {
          if (contentEl) {
            contentEl.style.transform = `scale(${1 - scale})`
            contentEl.style.boxShadow = `0 0 ${shadow}px ${shadow /
              5}px rgba(0, 0, 0, 0.3)`
          }

          return
        }

        if (this.lastKnownY > elementEndPoint) {
          if (contentEl) {
            contentEl.style.transform = `scale(${1})`
            contentEl.style.boxShadow = `0 0 0px 0px rgba(0, 0, 0, 0.3)`
          }

          return
        }

        const totalScrollAmount = elementEndPoint - elementStartPoint

        const scrollYAsAPercentageOfJourney =
          (this.lastKnownY - elementStartPoint) / totalScrollAmount

        const scaleAmount = 1 - scale + scale * scrollYAsAPercentageOfJourney
        const shadowAmount = shadow - shadow * scrollYAsAPercentageOfJourney

        if (contentEl) {
          contentEl.style.transform = `scale(${scaleAmount})`
          contentEl.style.boxShadow = `0 0 ${shadowAmount}px ${shadowAmount /
            5}px rgba(0, 0, 0, 0.3)`
        }
      }

      animateElements () {
        const productsEl = this.els.products?.element

        if (!productsEl) return

        const containerOffsetTop = getOffsetTop(productsEl)
        const animationOffset = 0.86
        const elementStartPoint =
          containerOffsetTop - window.innerHeight * animationOffset

        const totalScrollAmount = this.lastKnownY - elementStartPoint

        this.els['dynamic-element']?.elements.forEach(el => {
          const productCentre =
            getOffsetTop(el) - containerOffsetTop + el.offsetHeight / 2
          el.setAttribute('aria-hidden', productCentre > totalScrollAmount)
          el.dataset.offset = productCentre
        })
      }

      openProductDrawer (e) {
        e.preventDefault()

        const drawerContent = this.externalEls['product-drawer']?.element

        if (!drawerContent) return

        drawerContent.innerHTML = ''

        drawerContent.setAttribute('aria-busy', true)

        const productUrl = e.currentTarget.dataset.productUrl

        try {
          fetch(productUrl)
            .then(response => response.text())
            .then(text => {
              const parser = new DOMParser()
              const html = parser.parseFromString(text, 'text/html')

              const productDetails = html.querySelector('.prd-Product_Detail')
              productDetails.querySelector('.prd-Accordion').remove()

              const viewButton = document.createElement('a')
              viewButton.classList.add(
                'btn-Button',
                'btn-Button-tertiary',
                'btn-Button-fullWidth',
                'btn-Button-square'
              )
              viewButton.setAttribute('href', productUrl)
              viewButton.innerText = window.theme.strings.christmas.view_details
              productDetails
                .querySelector('.prd-Details_Buttons')
                .appendChild(viewButton)

              const productImage = html.querySelector(
                '[data-el="product-media.mediaContainer"]'
              )
              productImage.querySelector('a').setAttribute('href', productUrl)

              drawerContent.innerHTML = `
                ${productImage.outerHTML}
                ${productDetails.outerHTML}
              `
              drawerContent.setAttribute('aria-busy', false)
            })
        } catch (e) {
          drawerContent.innerHTML = `<p class="chr-Drawer_Error">${window.theme.strings.christmas.error_loading}</p>`
          drawerContent.setAttribute('aria-busy', false)
        }
      }

      initialiseProductCarousels () {
        if (window.innerWidth > 768) return

        const carousels = this.querySelectorAll(
          '[data-christmas-product-feature-el="product-slides"]'
        )

        carousels.forEach(carousel => {
          this.initialiseProductCarousel(carousel)
        })
      }

      initialiseProductCarousel (carousel) {
        const slideEls = carousel.querySelectorAll(
          '[data-christmas-product-feature-el="product-slide"]'
        )

        // eslint-disable-next-line new-cap
        new window.KeenSlider551(carousel, {
          slides: slideEls,
          slidesPerView: 1.2,
          spacing: 15,
          rubberband: false
        })
      }

      initialiseCollectionCarousel () {
        const slidesEl = this.els.slides?.element
        const slideEls = this.els.slide?.elements
        const scrollerEl = this.els.scroller?.element
        const scrollerContainerEl = this.els.scrollercontainer?.element

        if (slideEls.length < 2) return

        slidesEl.style.maxWidth = `${slideEls.length * 165 - 5}px`

        // eslint-disable-next-line new-cap
        const carousel = new window.KeenSlider551(slidesEl, {
          slides: '.car-Carousel_Slide',
          slidesPerView: 3.333,
          spacing: 5,
          rubberband: false,
          breakpoints: {
            '(min-width: 600px)': {
              slidesPerView: 4.333
            },
            '(min-width: 768px)': {
              slidesPerView: 5.333
            },
            '(min-width: 901px)': {
              slidesPerView: 6.333
            },
            '(min-width: 1201px)': {
              slidesPerView: 8
            }
          },
          created: () => {
            slidesEl.classList.remove('car-Carousel_Slides-hidden')
          },
          move: instance => {
            if (scrollerEl) {
              let progressTrack = instance.details().progressTrack
              progressTrack = progressTrack >= 1 ? 0 : progressTrack
              const movementPercentage = progressTrack * 100
              scrollerEl.style.setProperty(
                '--movement',
                `${movementPercentage}%`
              )
            }
          },
          afterChange: instance => {
            scrollerContainerEl?.setAttribute(
              'aria-hidden',
              instance.details().slidesPerView >= slideEls.length
            )
          }
        })
      }
    }
  )
}
